import { useEffect, useState } from "react";
import api from "@/api/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Building2, Plus, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function TenantsPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ name: "", code: "", admin_email: "", admin_password: "", admin_name: "" });
  const [editForm, setEditForm] = useState({ id: "", name: "", active: true });

  const load = async () => { const { data } = await api.get("/tenants"); setItems(data); };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/tenants", form); toast.success("Tenant created"); setOpen(false); setForm({ name: "", code: "", admin_email: "", admin_password: "", admin_name: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openEdit = (t) => {
    setEditForm({ id: t.id, name: t.name, active: t.active !== false });
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/tenants/${editForm.id}`, { name: editForm.name, active: editForm.active });
      toast.success("Tenant updated");
      setEditOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete tenant "${t.name}" (${t.code})? This will remove all its users, branches, parcels and history. This action cannot be undone.`)) return;
    try {
      await api.delete(`/tenants/${t.id}`);
      toast.success(`Tenant ${t.code} deleted`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline flex items-center gap-2"><Building2 size={12} /> Platform tenants</div>
          <h1 className="text-4xl font-display mt-1">Tenants.</h1>
        </div>
        <Button data-testid="tenant-new-btn" onClick={() => setOpen(true)} className="rounded-md bg-primary text-primary-foreground hover-brut shadow-brut">
          <Plus size={14} className="mr-2" /> New tenant
        </Button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tenants-grid">
        {items.map((t) => (
          <div key={t.id} className="border border-border bg-card p-6 hover-brut" data-testid={`tenant-card-${t.code}`}>
            <div className="flex items-center justify-between">
              <div className="font-display text-2xl">{t.name}</div>
              <span className="mono text-xs bg-foreground text-background px-2 py-0.5">{t.code}</span>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${t.active !== false ? "border-green-500 text-green-700 bg-green-50" : "border-slate-400 text-slate-500 bg-slate-50"}`}>
                {t.active !== false ? "Active" : "Inactive"}
              </span>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
              <div className="border border-border p-2"><div className="overline">Users</div><div className="mono text-lg">{t.user_count}</div></div>
              <div className="border border-border p-2"><div className="overline">Parcels</div><div className="mono text-lg">{t.parcel_count}</div></div>
            </div>
            <div className="mt-3 mono text-[10px] text-muted-foreground">Created {new Date(t.created_at).toLocaleDateString()}</div>
            <div className="mt-3 flex items-center gap-2">
              <button data-testid={`tenant-edit-${t.code}`} onClick={() => openEdit(t)}
                className="inline-flex items-center gap-1 text-xs border border-border px-2 py-1 rounded hover:border-primary hover:text-primary transition-colors">
                <Pencil size={12} /> Edit
              </button>
              <button data-testid={`tenant-delete-${t.code}`} onClick={() => remove(t)}
                className="inline-flex items-center gap-1 text-xs border border-border px-2 py-1 rounded hover:border-primary hover:text-primary transition-colors">
                <Trash2 size={12} /> Delete
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-muted-foreground text-sm">No tenants yet.</div>}
      </div>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-md max-w-md" aria-describedby="create-tenant-desc">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Create Tenant</DialogTitle>
            <p id="create-tenant-desc" className="text-xs text-muted-foreground">A dedicated company workspace with its own admin.</p>
          </DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <F label="Company name" v={form.name} onChange={(v) => setForm({ ...form, name: v })} t="ten-name" required />
            <F label="Short code (unique)" v={form.code} onChange={(v) => setForm({ ...form, code: v })} t="ten-code" required mono />
            <F label="Admin name" v={form.admin_name} onChange={(v) => setForm({ ...form, admin_name: v })} t="ten-admin-name" required />
            <F label="Admin email" v={form.admin_email} onChange={(v) => setForm({ ...form, admin_email: v })} t="ten-admin-email" required type="email" />
            <F label="Admin password" v={form.admin_password} onChange={(v) => setForm({ ...form, admin_password: v })} t="ten-admin-pw" required type="password" />
            <Button data-testid="ten-submit" type="submit" className="w-full rounded-md bg-primary text-primary-foreground hover-brut shadow-brut">Create tenant</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-md max-w-md" aria-describedby="edit-tenant-desc">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Edit Tenant</DialogTitle>
            <p id="edit-tenant-desc" className="text-xs text-muted-foreground">Update tenant name or toggle active state.</p>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-3">
            <F label="Company name" v={editForm.name} onChange={(v) => setEditForm({ ...editForm, name: v })} t="ten-edit-name" required />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="ten-edit-active" checked={editForm.active}
                onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })}
                className="accent-[#BE1E1E]" />
              <span>Active (users can log in)</span>
            </label>
            <Button data-testid="ten-edit-submit" type="submit" className="w-full rounded-md bg-primary text-primary-foreground hover-brut shadow-brut">Save changes</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, v, onChange, t, type = "text", required, mono }) {
  return (
    <label className="block">
      <span className="overline">{label}{required && <span className="text-primary"> *</span>}</span>
      <Input data-testid={t} value={v || ""} onChange={(e) => onChange(e.target.value)} required={required} type={type}
        className={`mt-1 rounded-md border-input focus-visible:ring-primary ${mono ? "font-mono" : ""}`} />
    </label>
  );
}
